import { OnboardingFlow } from "./types";

/**
 * Frontend source of truth for which onboarding pages exist and their order.
 * Reorder or remove entries here; add new page keys to `types.ts` and register
 * their components in `stepRegistry.ts`.
 *
 * The breadcrumb/progress header is derived from the ordered, de-duplicated
 * `groupLabel`s of the *effective* visible steps (see `useHook`). Steps that
 * belong to the same segment share a `groupLabel`; the workflows segment is
 * dropped automatically when the clinic has no workflows.
 *
 * `login` and `email_otp` are hidden auth steps and never appear in the
 * breadcrumb. `email_otp` is specifically the login SMS/custom-challenge OTP
 * screen and MUST stay at index 1 (right after `login`): a login challenge in
 * `LoginStep` advances to the next step, which needs to be the OTP screen.
 */
export const ONBOARDING_FLOW: OnboardingFlow = {
  flowKey: "cip_onboarding",
  version: 4,
  progressTotal: 5,
  steps: [
    {
      key: "login",
      showInProgress: false,
      title: "Create an account",
      fields: [
        {
          key: "sso",
          type: "single_select",
          label: "Sign up with PointClickCare",
        },
        {
          key: "email",
          type: "email",
          label: "Email",
          placeholder: "Johndoe@gmail.com",
        },
        {
          key: "password",
          type: "password",
          label: "Password",
          placeholder: "",
        },
      ],
      primaryCta: "Create your account",
    },
    {
      key: "email_otp",
      showInProgress: false,
      title: "Verify your email",
      fields: [{ key: "code", type: "otp" }],
      primaryCta: "Verify",
    },
    {
      key: "personalize",
      showInProgress: true,
      progressGroup: 1,
      groupLabel: "Personalization",
      title: "Let's get to know you",
      subtitle:
        "Tell us a little about yourself, and we'll set things up around the way you work.",
      fields: [
        {
          key: "full_name",
          type: "text",
          label: "Full Name",
          placeholder: "John Doe",
          required: true,
        },
        {
          key: "email",
          type: "email",
          label: "Email",
          placeholder: "jon@circadia.health",
          required: true,
        },
        {
          key: "role",
          type: "single_select",
          label: "Which describes you best?",
          required: true,
          options: [
            { key: "administrator", label: "Administrator" },
            { key: "director_of_nursing", label: "Director of Nursing" },
            {
              key: "assistant_director_of_nursing",
              label: "Assistant Director of Nursing",
            },
            {
              key: "nurse_manager_or_supervisor",
              label: "Nurse Manager or Supervisor",
            },
            { key: "licensed_nurse", label: "Licensed Nurse" },
            { key: "mds", label: "MDS" },
            { key: "medical_records", label: "Medical Records" },
            { key: "nurse_practitioner", label: "Nurse Practitioner" },
            { key: "attending_physician", label: "Attending Physician" },
            { key: "specialty_physician", label: "Specialty Physician" },
            { key: "admissions", label: "Admissions" },
            { key: "facility_operations", label: "Facility Operations" },
            { key: "business_office", label: "Business Office" },
            { key: "respiratory_therapist", label: "Respiratory Therapist" },
            { key: "other_therapy", label: "Other Therapy" },
          ],
        },
      ],
      primaryCta: "Continue",
    },
    {
      key: "facility",
      showInProgress: true,
      progressGroup: 1,
      groupLabel: "Personalization",
      title: "Pick your facility",
      subtitle:
        "Choose your facility and the floors you'd like to see. We'll keep everything focused there.",
      fields: [
        {
          key: "clinic_id",
          type: "facility",
          facilityScoped: true,
        },
      ],
      primaryCta: "Continue",
      secondaryCta: "Back",
    },
    {
      key: "goals",
      showInProgress: true,
      progressGroup: 2,
      groupLabel: "How we help",
      title: "How can Circadia help you?",
      subtitle:
        "Pick as many as you like. We’ll put these front and centre, and you can change your mind any time.",
      fields: [
        {
          key: "goals",
          type: "multi_select",
          options: [
            {
              key: "improve_clinical_quality",
              label: "Improve my clinical quality & outcomes",
              description:
                "Use the Circadia sensor combined with EMR data to flag high-risk patients.",
            },
            {
              key: "automate_clinical_audits",
              label: "Automate my clinical audits",
              description:
                "Run audits in the background for COCs, labs, and anything else that's top of mind.",
            },
            {
              key: "enhance_pdpm_reimbursement",
              label: "Enhance PDPM reimbursement",
              description:
                "Run an AI agent to analyze all new admissions for opportunities to enhance clinical reimbursement.",
            },
            {
              key: "prepare_annual_survey",
              label: "Preparing me for annual survey",
              description:
                "Analyze all your areas of opportunity to become bullet-proof for an audit.",
            },
            {
              key: "automate_vitals_entry",
              label: "Automate vital signs entry from my carts",
              description:
                "Save time capturing vital signs from carts and auto-populating them in the medical record.",
            },
          ],
        },
      ],
      primaryCta: "Continue",
      secondaryCta: "Back",
    },
    {
      key: "workflows",
      showInProgress: true,
      progressGroup: 3,
      groupLabel: "Workflows",
      title: "AI workflows to get you started",
      subtitle:
        "We work on these in the background, so the answers are waiting when you sit down.",
      fields: [
        {
          key: "workflows",
          type: "workflows",
          facilityScoped: true,
        },
      ],
      primaryCta: "Continue",
      secondaryCta: "Back",
      skippable: true,
      skipLabel: "Skip",
    },
    {
      key: "notifications",
      showInProgress: true,
      progressGroup: 4,
      groupLabel: "Notifications",
      title: "How would you like to use Circadia?",
      subtitle:
        "Tell us when you’re on and where to find you. Outside those hours, we’ll stay out of your way.",
      fields: [
        {
          key: "channels",
          type: "toggle_group",
          options: [
            {
              key: "sms",
              label: "Text",
              description:
                "Get information at your finger-tips. Strictly no PHI.",
              meta: { badge: "Highly Recommended" },
            },
            {
              key: "email",
              label: "Email",
              description:
                "Get daily, weekly, and monthly summary emails and reports. No spam.",
            },
            {
              key: "push",
              label: "Push",
              description:
                "Get notified on-demand. Available on mobile and desktop.",
            },
          ],
        },
      ],
      primaryCta: "Continue",
      secondaryCta: "Back",
      skippable: true,
      skipLabel: "Skip",
    },
    {
      key: "invite_team",
      showInProgress: true,
      progressGroup: 5,
      groupLabel: "Invite",
      title: "Invite your team",
      subtitle:
        "Circadia works best together. Add your teammates’ emails to bring them on board.",
      fields: [
        {
          key: "invitee_emails",
          type: "invite",
          placeholder: "Email address",
        },
      ],
      primaryCta: "Send Invites",
      secondaryCta: "Back",
      skippable: true,
      skipLabel: "Skip",
    },
  ],
};
